import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, ShieldCheck, FileText, PenLine } from "lucide-react";

const fcfa = (n) => (n == null ? "—" : Number(n).toLocaleString("fr-FR") + " FCFA");
const API = process.env.REACT_APP_BACKEND_URL;

export default function SignDevis() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const posOf = (e) => {
    const c = canvasRef.current, r = c.getBoundingClientRect(), t = e.touches?.[0];
    return { x: ((t ? t.clientX : e.clientX) - r.left) * (c.width / r.width), y: ((t ? t.clientY : e.clientY) - r.top) * (c.height / r.height) };
  };
  const startDraw = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = posOf(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const moveDraw = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = posOf(e); ctx.strokeStyle = "#0E2540"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
  const endDraw = () => { drawing.current = false; };
  const clearSig = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasDrawn(false); };

  const loadInfo = () => api.get(`/commercial/sign/${token}`).then(({ data }) => setInfo(data)).catch(() => setInfo({ error: true }));
  useEffect(() => { loadInfo(); }, [token]);

  const sign = async () => {
    if (!name.trim() || !accept) { toast.error("Indiquez votre nom et cochez « Bon pour accord »."); return; }
    setBusy(true);
    try {
      const signature_image = hasDrawn ? canvasRef.current.toDataURL("image/png") : "";
      await api.post(`/commercial/sign/${token}`, { name, role, accept, signature_image });
      toast.success("Devis signé. Merci !");
      loadInfo();
    } catch (e) { toast.error(apiError(e, "Signature impossible")); }
    finally { setBusy(false); }
  };

  if (!info) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="animate-spin text-[#0E7C7B]" size={28} /></div>;

  if (info.error) {
    return <div className="mx-auto flex min-h-[60vh] max-w-lg items-center px-4"><div className="w-full rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="sign-invalid"><p className="font-display text-xl font-bold text-navy">Lien invalide ou expiré</p><p className="mt-2 text-sm text-slate-500">Ce lien de signature n'est plus valide. Contactez le Cabinet CARIS Santé.</p></div></div>;
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-14" data-testid="sign-devis-page">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-navy px-8 py-6">
          <p className="text-lg font-bold text-white">CABINET CARIS SANTÉ</p>
          <p className="text-xs text-[#0E7C7B]">Vitalis Digital SIH — Devis {info.numero}</p>
        </div>

        <div className="p-8">
          <div className="rounded-2xl bg-slate-50 p-5">
            <div className="flex justify-between text-sm"><span className="text-slate-400">Établissement</span><span className="font-semibold text-navy">{info.client || "—"}</span></div>
            {info.expires_at && <div className="mt-1 flex justify-between text-sm"><span className="text-slate-400">Valable jusqu'au</span><span className="font-semibold text-navy">{fmtDate(info.expires_at)}</span></div>}
            <div className="mt-3 space-y-1 border-t border-slate-200 pt-3">
              {info.lignes.map((l, i) => (
                <div key={i} className="flex justify-between gap-4 text-sm"><span className="text-slate-600">{l.label}</span><span className="whitespace-nowrap font-medium text-navy">{fcfa(l.montant)}/an</span></div>
              ))}
              <div className="flex justify-between border-t border-slate-200 pt-2 text-sm font-bold"><span className="text-navy">Total annuel</span><span className="text-[#0E7C7B]">{fcfa(info.total)}/an</span></div>
            </div>
          </div>

          <a href={`${API}${info.pdf_url}`} target="_blank" rel="noreferrer" data-testid="sign-view-pdf" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#0E7C7B] hover:underline"><FileText size={15} /> Consulter le devis complet (PDF)</a>

          {info.signed ? (
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="sign-done">
              <ShieldCheck size={32} className="mx-auto text-emerald-600" />
              <p className="mt-2 font-display text-lg font-bold text-navy">Devis signé</p>
              <p className="mt-1 text-sm text-slate-600">Signé par <b>{info.signed_by}</b>{info.signed_at ? ` le ${fmtDate(info.signed_at)}` : ""}. Merci pour votre confiance !</p>
            </div>
          ) : (
            <div className="mt-6" data-testid="sign-form">
              <h2 className="flex items-center gap-2 font-display text-base font-semibold text-navy"><PenLine size={18} className="text-[#0E7C7B]" /> Signer en ligne</h2>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div><label className="mb-1 block text-xs font-medium text-slate-500">Nom complet *</label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Prénom Nom" data-testid="sign-name" /></div>
                <div><label className="mb-1 block text-xs font-medium text-slate-500">Fonction</label><Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Ex : Directeur" data-testid="sign-role" /></div>
              </div>
              <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-slate-600" data-testid="sign-accept">
                <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#0E7C7B]" />
                <span>Je confirme « Bon pour accord » sur ce devis au nom de l'établissement. Cette validation vaut signature électronique.</span>
              </label>

              <div className="mt-4">
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-500">Signature manuscrite (optionnelle)</label>
                  <button type="button" onClick={clearSig} data-testid="sign-clear" className="text-xs text-slate-400 hover:text-slate-600">Effacer</button>
                </div>
                <canvas ref={canvasRef} width={500} height={150} data-testid="sign-canvas"
                  onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
                  onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
                  className="w-full touch-none rounded-xl border border-dashed border-slate-300 bg-slate-50" style={{ height: 150 }} />
                <p className="mt-1 text-[11px] text-slate-400">Tracez votre signature dans le cadre ci-dessus (souris ou tactile).</p>
              </div>

              <Button onClick={sign} disabled={busy || !name.trim() || !accept} data-testid="sign-submit" className="mt-4 w-full rounded-full bg-[#0E7C7B] text-white hover:bg-[#0b6463]">
                {busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <PenLine size={15} className="mr-1" />} Signer le devis
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
